import { z } from "zod";

import { PHONE_LENGTHS } from "@/shared/config/contact";
import { getHomeCountryLabel } from "@/shared/config/localisation";
import { getNationalIdValidationMessage, isValidNationalId } from "@/shared/utils/nationalId";
import { createPersonNameSchema } from "@/shared/validation/nameSchemas";
import { emailSchema } from "./emailSchema";

const HEALTH_HABIT_VALUES = [
  "non_smoker",
  "regular_exercise",
  "healthy_diet",
  "occasional_alcohol",
  "no_alcohol",
  "stress_management",
  "adequate_sleep",
  "no_recreational_drugs",
  "maintains_healthy_weight",
  "regular_health_checkup",
  "vegetarian_vegan",
  "other",
] as const;

const YES_NO_VALUES = ["yes", "no"] as const;
const MEDICAL_CONDITION_VALUES = [
  "high_blood_pressure",
  "cholesterol",
  "diabetes",
  "asthma_respiratory_issues",
  "thyroid_disorder",
  "heart_disease",
  "joint_pain_arthritis",
  "liver_disease",
  "kidney_disease",
  "cancer",
  "mental_health_condition",
  "other",
] as const;

const OCCUPATION_TYPE_VALUES = [
  "salaried",
  "self_employed",
  "business_owner",
  "professional",
  "government_employee",
  "homemaker",
  "student",
  "retired",
  "farmer",
  "defense_personnel",
  "driver_transport",
  "factory_worker",
  "construction_worker",
  "others",
] as const;

const WORK_PROFILE_VALUES = [
  "desk_based",
  "field_based",
  "remote",
  "manual",
  "managerial",
  "professional",
  "sales_marketing",
  "industrial",
  "healthcare",
  "transport_logistics",
  "construction",
  "defense_security",
  "high_risk",
  "others",
] as const;

const ANNUAL_INCOME_RANGE_VALUES = [
  "below_50000",
  "50000_100000",
  "100000_250000",
  "250000_500000",
  "500000_plus",
] as const;

const calculateBmi = (heightCm: string, weightKg: string) => {
  const height = Number(heightCm);
  const weight = Number(weightKg);

  if (!height || !weight) return "";

  const bmi = weight / Math.pow(height / 100, 2);
  return bmi.toFixed(1);
};

const POLICY_TYPE_VALUES = ["newPolicy", "porting_policy"] as const;
const CURRENT_INSURER_VALUES = [
  "star_health",
  "hdfc_ergo",
  "icici_lombard",
  "niva_bupa",
  "care_health",
  "aditya_birla",
  "manipal_cigna",
  "tata_aig",
  "bajaj_allianz",
  "reliance_health",
  "daman",
  "orient_insurance",
  "axa_gulf",
  "bupa_arabia",
  "oman_insurance",
  "sukoon_insurance",
  "metlife_uae",
  "allianz_care",
  "salama",
  "adnic",
] as const;
const SUM_INSURED_VALUES = [
  "500000",
  "1000000",
  "1500000",
  "2000000",
  "5000000",
  "10000000",
  "20000000",
] as const;

export const healthPersonalDetailsSchema = z.object({
  firstName: createPersonNameSchema("First name"),
  lastName: createPersonNameSchema("Last name"),
  mobileNumber: z
    .object({
      countryCode: z.string().min(1, "Country code is required"),
      number: z.preprocess(
        (value) => (typeof value === "string" ? value.trim() : value),
        z
          .string()
          .min(1, "Enter a valid mobile number")
          .regex(/^\d+$/, "Please enter only digits. Eg. 501234567")
      ),
    })
    .superRefine((value, ctx) => {
      const number = typeof value.number === "string" ? value.number : "";
      const requiredLength = PHONE_LENGTHS[value.countryCode];

      if (requiredLength && number.length !== requiredLength) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Enter a valid ${requiredLength}-digit mobile number`,
          path: ["number"],
        });
      }

      if (value.countryCode === "971") {
        const allowedPrefixes = ["50", "52", "54", "55", "56", "58"];

        if (value.number.length !== 9) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Please enter only 9 digits Eg. 501234567.",
            path: ["number"],
          });
          return;
        }

        if (!allowedPrefixes.some((prefix) => value.number.startsWith(prefix))) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Mobile number must start with 50, 52, 54, 55, 56, or 58.",
            path: ["number"],
          });
        }

        return;
      }
    }),
  email: emailSchema,
  partnerCode: z
    .string()
    .optional()
    .refine((value) => !value || /^[A-Za-z0-9]{8}$/.test(value), {
      message: "Partner Code must be 8 alphanumeric characters",
    }),
  friendsAndFamilyContact: z
    .string()
    .optional()
    .refine((value) => !value || /^\d{9}$/.test(value), {
      message: "Friends & Family Contact must be a 9 digit number",
    }),
  marketingConsent: z
    .preprocess((value) => (typeof value === "string" ? value.trim() : value), z.string())
    .refine((value) => value === "yes" || value === "no", {
      message: "Please select Yes or No",
    })
    .transform((value) => value as "yes" | "no"),
});

export const healthDetailsSchema = z
  .object({
    coverageAmount: z.string().min(1, "Please select coverage amount"),
    policyType: z.enum(POLICY_TYPE_VALUES, {
      message: "Please select policy type",
    }),
    coverageType: z.union([
      z.literal("individual"),
      z.literal("family"),
      z.literal("family_floater"),
    ]),
    currentInsurer: z.union([z.literal(""), z.enum(CURRENT_INSURER_VALUES)]),
    policyExpiryDate: z.string(),
    sumInsured: z.union([z.literal(""), z.enum(SUM_INSURED_VALUES)]),
    claimsInLast3Years: z.union([z.literal(""), z.enum(YES_NO_VALUES)]),
    members: z
      .array(
        z.object({
          name: z.string().optional(),
          relationship: z
            .string()
            .min(1, "Relationship is required")
            .refine(
              (val) =>
                [
                  "self",
                  "spouse",
                  "child",
                  "parent",
                  "son",
                  "daughter",
                  "mother",
                  "father",
                ].includes(val),
              "Invalid relationship"
            ),
          dob: z.string().min(1, "Date of birth is required"),
          preExistingCondition: z.boolean(),
        })
      )
      .superRefine((members, ctx) => {
        members.forEach((member, index) => {
          const name = typeof member.name === "string" ? member.name.trim() : "";
          const label = index === 0 ? "Primary member name" : `Member ${index + 1} name`;

          if (!name) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} is required`,
              path: [index, "name"],
            });
            return;
          }

          if (name.length < 2) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be at least 2 characters`,
              path: [index, "name"],
            });
            return;
          }

          if (name.length > 50) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} cannot exceed 50 characters`,
              path: [index, "name"],
            });
            return;
          }

          if (!/^[A-Za-z ]+$/.test(name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must contain only letters`,
              path: [index, "name"],
            });
            return;
          }

          if (/\s{2,}/.test(name)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} cannot contain consecutive spaces`,
              path: [index, "name"],
            });
            return;
          }

          if (/(.)\1{4,}/i.test(name.replace(/\s/g, ""))) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} contains too many repeated characters`,
              path: [index, "name"],
            });
          }
        });
      })
      .min(1, "At least one member is required"),
    startDate: z
      .string()
      .min(1, "Start date is required")
      .refine((val) => {
        const date = new Date(val);
        return !Number.isNaN(date.getTime());
      }, "Please enter a valid start date"),
    policy_tenure: z.enum(["1", "2", "3", "4", "5"], {
      message: "Please select policy_tenure",
    }),
  })
  .superRefine((data, ctx) => {
    // Age Validation

    const policyStartDate = new Date(data.startDate);

    data.members.forEach((member, index) => {
      if (!member.dob) return;

      const dob = new Date(member.dob);

      if (Number.isNaN(dob.getTime())) {
        return;
      }

      const age = Math.floor(
        (policyStartDate.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25)
      );

      // Self / Spouse
      if (["self", "spouse"].includes(member.relationship) && age < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["members", index, "dob"],
          message: `${member.relationship} must be at least 18 years old`,
        });
      }

      // Parent / Mother / Father
      if (["parent", "mother", "father"].includes(member.relationship) && age < 36) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["members", index, "dob"],
          message: `${member.relationship} must be at least 36 years old`,
        });
      }

      // Child / Son / Daughter
      const ageInDays = Math.floor(
        (policyStartDate.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24)
      );

      const ageInYears =
        (policyStartDate.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

      if (["child", "son", "daughter"].includes(member.relationship)) {
        const ageInDays = Math.floor(
          (policyStartDate.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (ageInDays < 17) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["members", index, "dob"],
            message: `${member.relationship} must be at least 17 days old`,
          });
        }

        if (ageInYears > 30) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["members", index, "dob"],
            message: `${member.relationship} must not exceed 30 years of age`,
          });
        }
      }
    });

    // Existing Porting Policy Validation

    if (data.policyType !== "porting_policy") {
      return;
    }

    if (!data.currentInsurer.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select current insurer",
        path: ["currentInsurer"],
      });
    } else if (
      !CURRENT_INSURER_VALUES.includes(
        data.currentInsurer as (typeof CURRENT_INSURER_VALUES)[number]
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a valid current insurer",
        path: ["currentInsurer"],
      });
    }

    if (!data.policyExpiryDate.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Policy expiry date is required",
        path: ["policyExpiryDate"],
      });
    } else if (
      !/^\d{4}-\d{2}-\d{2}$/.test(data.policyExpiryDate) ||
      Number.isNaN(new Date(data.policyExpiryDate).getTime())
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please enter a valid policy expiry date",
        path: ["policyExpiryDate"],
      });
    }

    if (!data.sumInsured.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select sum insured",
        path: ["sumInsured"],
      });
    } else if (
      !SUM_INSURED_VALUES.includes(data.sumInsured as (typeof SUM_INSURED_VALUES)[number])
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a valid sum insured",
        path: ["sumInsured"],
      });
    }

    if (!data.claimsInLast3Years) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select an answer",
        path: ["claimsInLast3Years"],
      });
    }
  });

export const healthHabitsSchema = z.object({
  selectedHabits: z
    .array(z.enum(HEALTH_HABIT_VALUES))
    .min(1, "Please select at least one health habit"),
});

export const healthQuestionsSchema = z
  .object({
    preExistingMedicalConditions: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    hospitalizedLast2Years: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    currentlyOnMedication: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    surgeryLast5Years: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    allergies: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    tobaccoOrNicotine: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    familyHistoryMajorIllness: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
    otherMedicalCondition: z.enum(YES_NO_VALUES, {
      message: "Please select an answer",
    }),
  })
  .strict();

export const healthQuickQuestionsSchema = z
  .object({
    heightCm: z
      .string()
      .trim()
      .min(1, "Height is required")
      .refine((value) => /^\d+(\.\d{1,2})?$/.test(value), "Please enter a valid height")
      .refine((value) => {
        const height = Number(value);
        return height >= 120 && height <= 220;
      }, "Height must be between 120 cm and 220 cm"),

    weightKg: z
      .string()
      .trim()
      .min(1, "Weight is required")
      .refine((value) => /^\d+(\.\d{1,2})?$/.test(value), "Please enter a valid weight")
      .refine((value) => {
        const weight = Number(value);
        return weight >= 1 && weight <= 150;
      }, "Weight must be between 1 kg and 150 kg"),
    bmi: z.string().trim().optional(),
    medicalConditions: z
      .array(z.enum(MEDICAL_CONDITION_VALUES))
      .min(1, "Please select at least one medical condition"),
    occupationType: z.enum(OCCUPATION_TYPE_VALUES, {
      message: "Please select occupation type",
    }),
    workProfile: z.enum(WORK_PROFILE_VALUES, {
      message: "Please select work profile",
    }),
    annualIncomeRange: z.enum(ANNUAL_INCOME_RANGE_VALUES, {
      message: "Please select annual income range",
    }),
    additionalInformation: z
      .string()
      .max(500, "Additional information must not exceed 500 characters"),
    otherMedicalConditionDetails: z
      .string()
      .max(120, "Specify condition must not exceed 120 characters"),
  })
  .superRefine((data, ctx) => {
    const hasHeight = Boolean(data.heightCm?.trim());
    const hasWeight = Boolean(data.weightKg?.trim());

    if (!hasHeight || !hasWeight) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "BMI is auto-calculated after entering height and weight",
        path: ["bmi"],
      });
      return;
    }

    const expectedBmi = calculateBmi(data.heightCm, data.weightKg);

    if (!expectedBmi) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "BMI is auto-calculated after entering height and weight",
        path: ["bmi"],
      });
      return;
    }

    if (!data.bmi || data.bmi !== expectedBmi) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "BMI does not match the selected height and weight",
        path: ["bmi"],
      });
    }

    if (data.medicalConditions.includes("other") && !data.otherMedicalConditionDetails.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify the other medical condition",
        path: ["otherMedicalConditionDetails"],
      });
    }
  });

export const healthLifestyleSchema = healthHabitsSchema.merge(healthQuestionsSchema);
export const healthAdditionalInfoSchema = z.object({
  gender: z.enum(["", "Male", "Female"]).refine((val) => val !== "", {
    message: "Please select gender",
  }),
  nationality: z.string().min(1, "Nationality is required"),
  cityOfResidence: z
    .string()
    .min(1, "City of residence is required")
    .regex(/^[A-Za-z\s.-]+$/, "Enter a valid city name"),
  address: z
    .string()
    .min(1, "Address is required")
    .max(200, "Address must not exceed 200 characters."),
  emiratesId: z.string().refine((value) => isValidNationalId(value), {
    message: getNationalIdValidationMessage(),
  }),
  passportNumber: z.string().optional(),
  sourceOfFund: z.string().min(1, "Source of fund is required"),
  beneficiaryName: createPersonNameSchema("Beneficiary name"),
  beneficiaryRelation: z
    .string()
    .min(1, "Beneficiary relation is required")
    .refine((val) => ["Legal Heir", "Other"].includes(val), {
      message: "Invalid beneficiary relation",
    }),
  uaeResidentConfirm: z.boolean().refine((val) => val === true, {
    message: `Please confirm ${getHomeCountryLabel()} residency`,
  }),
});
